import {
  AggregationType,
  type BarTimeSeries,
  type DataSource,
  type LineTimeSeries,
  MethodType,
  type ScatterTimeSeries,
  SymbolType,
  type TimeSeries,
  type Trendline,
  TrendlineType,
} from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import { uuid, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { FormSection } from '../../../../components/forms/FormSection';
import { HookedSubmitButton } from '../../../../components/forms/V3HookedSubmitButton';
import { zodResolver } from '../../../../utils/zod';
import type { UseTimeseriesChartReturn } from '../../../participantData/hooks/useTimeseriesChart';
import {
  AGGREGATION_TYPE_OPTIONS,
  COLORS,
  METHOD_TYPE_OPTIONS,
  SYMBOL_TYPE_OPTIONS,
} from '../../../participantData/utils';
import { TimeSeriesForm } from './TimeSeriesForm';

type FormValues = TimeSeries;

export const trendlineSchema: z.ZodSchema<Trendline> = z.object({
  __typename: z.literal('Trendline'),
  id: z.string(),
  name: z.string().nonempty('Please enter a name'),
  type: z.enum(TrendlineType),
  color: z.string(),
});

const sourceSchema: z.ZodSchema<DataSource> = z.object({
  __typename: z.literal('ParticipantBehaviourDataSource'),
  method: z.enum(MethodType),
  participantBehaviourId: z.string(),
});

const lineTimeSeriesSchema: z.ZodSchema<LineTimeSeries> = z.object({
  __typename: z.literal('LineTimeSeries'),
  id: z.string(),
  source: sourceSchema,
  color: z.string(),
  aggregation: z.enum(AggregationType),
  symbol: z.enum(SymbolType),
  trendline: z.nullable(trendlineSchema),
});

const scatterTimeSeriesSchema: z.ZodSchema<ScatterTimeSeries> = z.object({
  __typename: z.literal('ScatterTimeSeries'),
  id: z.string(),
  source: sourceSchema,
  color: z.string(),
  aggregation: z.enum(AggregationType),
  symbol: z.enum(SymbolType),
  trendline: z.nullable(trendlineSchema),
});

const barTimeSeriesSchema: z.ZodSchema<BarTimeSeries> = z.object({
  __typename: z.literal('BarTimeSeries'),
  id: z.string(),
  source: sourceSchema,
  color: z.string(),
  aggregation: z.enum(AggregationType),
});

export const timeSeriesSchema: z.ZodSchema<TimeSeries> = z.union([
  barTimeSeriesSchema,
  lineTimeSeriesSchema,
  scatterTimeSeriesSchema,
]);

interface TimeSeriesAddFormProps {
  participantBehaviourId: string;
  chart: UseTimeseriesChartReturn;
  onClose: () => void;
}

export const TimeSeriesAddForm = (props: TimeSeriesAddFormProps) => {
  const { participantBehaviourId, chart, onClose } = props;
  const methodType = METHOD_TYPE_OPTIONS[0]!;
  const aggregationType = AGGREGATION_TYPE_OPTIONS[0]!;
  const symbolType = SYMBOL_TYPE_OPTIONS[0]!;
  const redColor = COLORS.red;

  const initialValues: TimeSeries = {
    __typename: 'LineTimeSeries',
    id: uuid(),
    source: {
      __typename: 'ParticipantBehaviourDataSource',
      method: methodType.raw,
      participantBehaviourId,
    },
    color: redColor,
    aggregation: aggregationType.raw,
    symbol: symbolType.raw,
    trendline: null,
  };

  const methods = useForm<TimeSeries>({
    defaultValues: initialValues,
    resolver: zodResolver(timeSeriesSchema),
  });

  const { reset } = methods;

  const onSubmit = (values: FormValues) => {
    chart.onAddSeries(values);
    reset({
      ...initialValues,
      id: uuid(),
    });
    onClose();
  };

  if (!methodType || !redColor || !aggregationType || !symbolType) {
    return null;
  }

  return (
    <ChakraV3.Stack
      bg='white'
      gap={0}
      w='lg'
      layerStyle='bordered'
      shadow='2xl'
      rounded='md'
      overflow='hidden'
      p={4}
    >
      <FormProvider {...methods}>
        <FormSection as='form' heading='Add series' onSubmit={methods.handleSubmit(onSubmit)}>
          <ChakraV3.Separator />
          <TimeSeriesForm chart={chart} />
          <ChakraV3.Separator />
          <HookedSubmitButton w='full' size='sm' disabled={false}>
            Add
          </HookedSubmitButton>
        </FormSection>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

interface Props {
  participantBehaviourId: string;
  chart: UseTimeseriesChartReturn;
}

export const TimeSeriesAddControl = (props: Props) => {
  const { participantBehaviourId, chart } = props;

  const { open, onOpen, onClose } = ChakraV3.useDisclosure();

  return (
    <FloatingPopover
      open={open}
      setOpen={(v) => (v ? onOpen() : onClose())}
      render={() => (
        <TimeSeriesAddForm
          participantBehaviourId={participantBehaviourId}
          chart={chart}
          onClose={onClose}
        />
      )}
    >
      <ChakraV3.Button variant='outline' borderStyle='dashed' size='sm' onClick={onOpen}>
        Add series
        <FaPlus />
      </ChakraV3.Button>
    </FloatingPopover>
  );
};
